import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, Plus, Trash2, Search, Download, Globe, Settings, Factory, AlertTriangle, DollarSign, TrendingUp, ChevronDown, ChevronUp, BarChart3, Building2, ShieldCheck, ShieldAlert, Shield } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { RiskScoreLegend } from '@/components/RiskScoreLegend';
import api from '@/lib/api';
import { getRiskBand, getDisruptionEstimate } from '@/lib/utils';
import { NavBar } from '@/components/NavBar';

const COUNTRY_FLAGS = {"CN":"🇨🇳","ID":"🇮🇩","VN":"🇻🇳","JP":"🇯🇵","KR":"🇰🇷","IN":"🇮🇳","TH":"🇹🇭","MY":"🇲🇾","PH":"🇵🇭","TW":"🇹🇼","SG":"🇸🇬","BD":"🇧🇩","MM":"🇲🇲","KH":"🇰🇭","PK":"🇵🇰","LK":"🇱🇰","PG":"🇵🇬"};

const SuppliersPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [quickFilter, setQuickFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState(searchParams.get('country') || '');
  const [sectorFilter, setSectorFilter] = useState('');
  const [sortBy, setSortBy] = useState('risk_desc');
  const [breakdownView, setBreakdownView] = useState('country');
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ supplier_name: '', country: '', city: '', primary_sector: '', annual_spend_aud: '' });
  const [uploading, setUploading] = useState(false);

  useEffect(() => { loadSuppliers(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSuppliers = async () => {
    try { setSuppliers((await api.getSuppliers()).data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // === SUMMARY CALCULATIONS ===
  const summary = useMemo(() => {
    if (!suppliers.length) return null;
    const total = suppliers.length;
    const countries = new Set(suppliers.map(s => s.country_code)).size;
    const totalSpend = suppliers.reduce((a, s) => a + (s.annual_spend_aud || 0), 0);
    const bands = { Low: 0, Moderate: 0, Elevated: 0, High: 0, Critical: 0 };
    suppliers.forEach(s => { const b = getRiskBand(s.risk_score || 0); bands[b.level] = (bands[b.level] || 0) + 1; });
    const elevatedPlus = bands.Elevated + bands.High + bands.Critical;
    const highestRisk = suppliers.reduce((a, s) => (!a || (s.risk_score || 0) > (a.risk_score || 0)) ? s : a, null);
    const spendAtRisk = suppliers.filter(s => (s.risk_score || 0) >= 50).reduce((a, s) => a + (s.annual_spend_aud || 0), 0);
    const spendPctAtRisk = totalSpend > 0 ? Math.round((spendAtRisk / totalSpend) * 100) : 0;
    const weightedScore = totalSpend > 0 ? suppliers.reduce((a, s) => a + (s.risk_score || 0) * (s.annual_spend_aud || 0), 0) / totalSpend : suppliers.reduce((a, s) => a + (s.risk_score || 0), 0) / total;
    // Concentration
    const countrySpend = {};
    suppliers.forEach(s => { const cn = s.country_name || s.country_code; countrySpend[cn] = (countrySpend[cn] || 0) + (s.annual_spend_aud || 0); });
    const topCountry = Object.entries(countrySpend).sort((a, b) => b[1] - a[1])[0];
    const concPct = totalSpend > 0 && topCountry ? Math.round((topCountry[1] / totalSpend) * 100) : 0;
    return { total, countries, totalSpend, bands, elevatedPlus, highestRisk, spendAtRisk, spendPctAtRisk, weightedScore, topCountry: topCountry?.[0], concPct };
  }, [suppliers]);

  // === BREAKDOWN BY COUNTRY/SECTOR ===
  const countryGroups = useMemo(() => {
    const map = {};
    suppliers.forEach(s => {
      const k = s.country_name || s.country_code || 'Unknown';
      if (!map[k]) map[k] = { name: k, code: s.country_code, flag: COUNTRY_FLAGS[s.country_code] || '', suppliers: [], totalSpend: 0, scores: [] };
      map[k].suppliers.push(s);
      map[k].totalSpend += s.annual_spend_aud || 0;
      map[k].scores.push(s.risk_score || 0);
    });
    const totalSpend = suppliers.reduce((a, s) => a + (s.annual_spend_aud || 0), 0);
    return Object.values(map).map(g => ({
      ...g, count: g.suppliers.length, avgScore: Math.round(g.scores.reduce((a, b) => a + b, 0) / g.scores.length),
      spendPct: totalSpend > 0 ? Math.round((g.totalSpend / totalSpend) * 100) : 0,
      riskDist: { Low: g.scores.filter(s => s < 30).length, Mod: g.scores.filter(s => s >= 30 && s < 50).length, Elev: g.scores.filter(s => s >= 50 && s < 70).length, High: g.scores.filter(s => s >= 70).length },
    })).sort((a, b) => b.totalSpend - a.totalSpend);
  }, [suppliers]);

  const sectorGroups = useMemo(() => {
    const map = {};
    suppliers.forEach(s => {
      const k = s.primary_sector || 'General';
      if (!map[k]) map[k] = { name: k, suppliers: [], totalSpend: 0, scores: [], countryCodes: new Set() };
      map[k].suppliers.push(s);
      map[k].totalSpend += s.annual_spend_aud || 0;
      map[k].scores.push(s.risk_score || 0);
      if (s.country_code) map[k].countryCodes.add(s.country_code);
    });
    const totalSpend = suppliers.reduce((a, s) => a + (s.annual_spend_aud || 0), 0);
    return Object.values(map).map(g => ({
      ...g, count: g.suppliers.length, avgScore: Math.round(g.scores.reduce((a, b) => a + b, 0) / g.scores.length),
      spendPct: totalSpend > 0 ? Math.round((g.totalSpend / totalSpend) * 100) : 0,
      countries: Array.from(g.countryCodes),
    })).sort((a, b) => b.totalSpend - a.totalSpend);
  }, [suppliers]);

  // === FILTERED TABLE ===
  const filtered = useMemo(() => {
    let result = [...suppliers];
    if (search) { const s = search.toLowerCase(); result = result.filter(r => r.supplier_name?.toLowerCase().includes(s) || r.country_name?.toLowerCase().includes(s) || r.primary_sector?.toLowerCase().includes(s)); }
    if (quickFilter === 'high_risk') result = result.filter(r => (r.risk_score || 0) >= 50);
    if (quickFilter === 'tier1') result = result.filter(r => r.tier === 'Tier 1');
    if (countryFilter) result = result.filter(r => r.country_name === countryFilter);
    if (sectorFilter) result = result.filter(r => r.primary_sector === sectorFilter);
    if (sortBy === 'risk_desc') result.sort((a, b) => (b.risk_score || 0) - (a.risk_score || 0));
    else if (sortBy === 'risk_asc') result.sort((a, b) => (a.risk_score || 0) - (b.risk_score || 0));
    else if (sortBy === 'spend') result.sort((a, b) => (b.annual_spend_aud || 0) - (a.annual_spend_aud || 0));
    else if (sortBy === 'name') result.sort((a, b) => (a.supplier_name || '').localeCompare(b.supplier_name || ''));
    return result;
  }, [suppliers, search, quickFilter, countryFilter, sectorFilter, sortBy]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData(); formData.append('file', file);
      await api.uploadSuppliers(formData);
      toast.success('Suppliers uploaded successfully');
      setShowUpload(false); loadSuppliers();
    } catch (err) { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleAddSupplier = async () => {
    try {
      await api.createSupplier({ ...newSupplier, annual_spend_aud: Number(newSupplier.annual_spend_aud) || 0 });
      toast.success('Supplier added');
      setShowAdd(false); setNewSupplier({ supplier_name: '', country: '', city: '', primary_sector: '', annual_spend_aud: '' }); loadSuppliers();
    } catch (err) { toast.error('Failed to add supplier'); }
  };

  const fmtSpend = (v) => v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}K` : `$${v}`;
  const uniqueCountries = [...new Set(suppliers.map(s => s.country_name).filter(Boolean))].sort();
  const uniqueSectors = [...new Set(suppliers.map(s => s.primary_sector).filter(Boolean))].sort();

  if (loading) return <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center"><div className="animate-pulse text-[#4E6974]">Loading suppliers...</div></div>;

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <NavBar />

      <main className="pt-24 px-4 lg:px-6 pb-8">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center justify-between mt-8 mb-4">
            <div>
              <h1 className="text-3xl font-bold text-[#0D3B4F]" data-testid="suppliers-title">My suppliers</h1>
              <p className="text-[#4E6974] mt-1">{suppliers.length} suppliers across {summary?.countries || 0} countries</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowUpload(true)} data-testid="upload-csv-btn"><Upload className="h-4 w-4 mr-1" />Upload CSV</Button>
              <Button size="sm" className="bg-[#00707C] text-white" onClick={() => setShowAdd(true)} data-testid="add-supplier-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
            </div>
          </div>

          {/* === SUMMARY CARDS === */}
          {summary && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="supplier-summary-cards">
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardContent className="p-4 text-center">
                  <p className="text-[10px] text-[#4E6974] uppercase tracking-wide">Total suppliers</p>
                  <p className="text-3xl font-bold text-[#0D3B4F] mt-1" data-testid="summary-total">{summary.total}</p>
                  <p className="text-xs text-[#4E6974]">across {summary.countries} countries</p>
                </CardContent>
              </Card>
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardContent className="p-4">
                  <p className="text-[10px] text-[#4E6974] uppercase tracking-wide mb-2">Risk distribution</p>
                  <div className="flex h-3 rounded-full overflow-hidden gap-0.5" data-testid="summary-risk-bar">
                    {[['Low','#2E7D32'],['Moderate','#FF9500'],['Elevated','#E65100'],['High','#FF3B30'],['Critical','#8B0000']].map(([l,c]) =>
                      summary.bands[l] > 0 && <div key={l} style={{ width: `${(summary.bands[l] / summary.total) * 100}%`, backgroundColor: c }} title={`${l}: ${summary.bands[l]}`} />
                    )}
                  </div>
                  <p className="text-[10px] text-[#4E6974] mt-2">{summary.elevatedPlus} of {summary.total} at elevated risk or above{summary.highestRisk ? `. Highest: ${summary.highestRisk.supplier_name} (${Math.round(summary.highestRisk.risk_score)})` : ''}</p>
                </CardContent>
              </Card>
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardContent className="p-4 text-center">
                  <p className="text-[10px] text-[#4E6974] uppercase tracking-wide">Total spend</p>
                  <p className="text-2xl font-bold text-[#0D3B4F] mt-1" data-testid="summary-spend">{fmtSpend(summary.totalSpend)}</p>
                  {summary.spendAtRisk > 0 && <p className="text-xs text-[#E65100] font-medium">{fmtSpend(summary.spendAtRisk)} at elevated risk</p>}
                  <p className="text-[10px] text-[#4E6974]">{summary.spendPctAtRisk}% of spend at risk</p>
                </CardContent>
              </Card>
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardContent className="p-4 text-center">
                  <p className="text-[10px] text-[#4E6974] uppercase tracking-wide">Avg risk score</p>
                  <div className="flex items-center justify-center gap-2 mt-1">
                    <span className="text-3xl font-bold text-[#0D3B4F]" data-testid="summary-avg-risk">{Math.round(summary.weightedScore)}</span>
                    <Badge className={`${getRiskBand(summary.weightedScore).bg} ${getRiskBand(summary.weightedScore).text} text-xs`}>{getRiskBand(summary.weightedScore).level}</Badge>
                  </div>
                  <p className="text-[10px] text-[#4E6974] mt-1">{getRiskBand(summary.weightedScore).desc.split('.')[0]}.</p>
                </CardContent>
              </Card>
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardContent className="p-4 text-center">
                  <p className="text-[10px] text-[#4E6974] uppercase tracking-wide">Concentration risk</p>
                  {summary.topCountry && (
                    <>
                      <p className="text-lg font-bold mt-1" style={{ color: summary.concPct > 40 ? '#FF3B30' : summary.concPct > 25 ? '#FF9500' : '#0D3B4F' }} data-testid="summary-concentration">{summary.concPct}%</p>
                      <p className="text-xs text-[#4E6974]">of spend in {summary.topCountry}</p>
                      {summary.concPct > 40 && <p className="text-[10px] text-[#FF3B30] mt-1">High concentration — consider diversifying</p>}
                    </>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* === BREAKDOWN TOGGLE === */}
          {suppliers.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Button variant={breakdownView === 'country' ? 'default' : 'outline'} size="sm" onClick={() => setBreakdownView('country')} className={breakdownView === 'country' ? 'bg-[#00707C] text-white' : ''} data-testid="breakdown-country-btn">
                  <Globe className="h-3 w-3 mr-1" /> By country
                </Button>
                <Button variant={breakdownView === 'sector' ? 'default' : 'outline'} size="sm" onClick={() => setBreakdownView('sector')} className={breakdownView === 'sector' ? 'bg-[#00707C] text-white' : ''} data-testid="breakdown-sector-btn">
                  <Building2 className="h-3 w-3 mr-1" /> By sector
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {(breakdownView === 'country' ? countryGroups : sectorGroups).map((g) => {
                  const band = getRiskBand(g.avgScore);
                  const isExpanded = expandedGroup === g.name;
                  return (
                    <Card key={g.name} className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none hover:shadow-[0_8px_32px_rgba(0,0,0,0.1)] transition-all cursor-pointer"
                      onClick={() => { setExpandedGroup(isExpanded ? null : g.name); setCountryFilter(breakdownView === 'country' && !isExpanded ? g.name : ''); setSectorFilter(breakdownView === 'sector' && !isExpanded ? g.name : ''); }}
                      data-testid={`breakdown-card-${g.name.toLowerCase().replace(/\s+/g, '-')}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {breakdownView === 'country' && <span className="text-xl">{g.flag}</span>}
                            {breakdownView === 'sector' && <Factory className="h-5 w-5 text-[#0097A7]" />}
                            <div>
                              <h3 className="text-sm font-bold text-[#0D3B4F]">{g.name}</h3>
                              <p className="text-[10px] text-[#4E6974]">{g.count} supplier{g.count !== 1 ? 's' : ''} &middot; {fmtSpend(g.totalSpend)}</p>
                            </div>
                          </div>
                          <Badge className={`${band.bg} ${band.text} text-xs`}>{g.avgScore}</Badge>
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-[#4E6974]">
                          <span>{g.spendPct}% of total spend</span>
                          {breakdownView === 'sector' && g.countries && <span className="flex gap-0.5">{g.countries.map(c => <span key={c}>{COUNTRY_FLAGS[c] || c}</span>)}</span>}
                        </div>
                        {breakdownView === 'country' && g.riskDist && (
                          <div className="flex h-1.5 rounded-full overflow-hidden gap-0.5 mt-2">
                            {g.riskDist.Low > 0 && <div style={{ flex: g.riskDist.Low, backgroundColor: '#2E7D32' }} />}
                            {g.riskDist.Mod > 0 && <div style={{ flex: g.riskDist.Mod, backgroundColor: '#FF9500' }} />}
                            {g.riskDist.Elev > 0 && <div style={{ flex: g.riskDist.Elev, backgroundColor: '#E65100' }} />}
                            {g.riskDist.High > 0 && <div style={{ flex: g.riskDist.High, backgroundColor: '#FF3B30' }} />}
                          </div>
                        )}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mt-3 pt-3 border-t border-[#E0E0E0]">
                              {g.suppliers.map(s => {
                                const sb = getRiskBand(s.risk_score || 0);
                                return (
                                  <div key={s.id} className="flex items-center justify-between py-1.5 text-sm cursor-pointer hover:text-[#0097A7]" onClick={(e) => { e.stopPropagation(); navigate(`/suppliers/${s.id}`); }}>
                                    <span className="text-[#0D3B4F]">{s.supplier_name}</span>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-[#4E6974]">{fmtSpend(s.annual_spend_aud || 0)}</span>
                                      <Badge className={`${sb.bg} ${sb.text} text-[10px]`}>{Math.round(s.risk_score || 0)}</Badge>
                                    </div>
                                  </div>
                                );
                              })}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* === SUPPLIER TABLE === */}
          <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
            <CardContent className="p-4">
              {/* Search + Quick Filters */}
              <div className="flex flex-col gap-3 mb-4">
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4E6974]" />
                    <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, country, or sector..." className="pl-9" data-testid="supplier-search" />
                  </div>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-[160px]" data-testid="supplier-sort"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="risk_desc">Highest risk</SelectItem>
                      <SelectItem value="risk_asc">Lowest risk</SelectItem>
                      <SelectItem value="spend">Highest spend</SelectItem>
                      <SelectItem value="name">Name A-Z</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {[['all','All'],['high_risk','Elevated risk+'],['tier1','Tier 1 only']].map(([k,l]) => (
                    <Button key={k} variant={quickFilter === k ? 'default' : 'outline'} size="sm" className={quickFilter === k ? 'bg-[#0D3B4F] text-white' : 'text-[#4E6974]'}
                      onClick={() => { setQuickFilter(k); setCountryFilter(''); setSectorFilter(''); }} data-testid={`filter-${k}`}>{l}</Button>
                  ))}
                  <Select value={countryFilter} onValueChange={v => { setCountryFilter(v === 'all_c' ? '' : v); setQuickFilter('all'); }}>
                    <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue placeholder="Country" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_c">All countries</SelectItem>
                      {uniqueCountries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={sectorFilter} onValueChange={v => { setSectorFilter(v === 'all_s' ? '' : v); setQuickFilter('all'); }}>
                    <SelectTrigger className="w-[150px] h-8 text-xs"><SelectValue placeholder="Sector" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_s">All sectors</SelectItem>
                      {uniqueSectors.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <RiskScoreLegend compact />
                </div>
              </div>

              {/* Table */}
              {filtered.length === 0 ? (
                <div className="text-center py-12 text-[#4E6974]">
                  {suppliers.length === 0 ? (
                    <div>
                      <Factory className="h-12 w-12 mx-auto text-[#E0E0E0] mb-3" />
                      <p className="text-lg font-medium text-[#0D3B4F]">No suppliers yet</p>
                      <p className="text-sm mt-1">Upload a CSV or add suppliers manually to get started</p>
                      <div className="flex justify-center gap-3 mt-4">
                        <Button onClick={() => setShowUpload(true)}><Upload className="h-4 w-4 mr-1" />Upload CSV</Button>
                        <Button variant="outline" onClick={() => setShowAdd(true)}><Plus className="h-4 w-4 mr-1" />Add supplier</Button>
                      </div>
                    </div>
                  ) : <p>No suppliers match your filters</p>}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="supplier-table">
                    <thead>
                      <tr className="border-b border-[#E0E0E0]">
                        <th className="text-left py-2 px-3 text-[#4E6974] font-medium">Supplier</th>
                        <th className="text-left py-2 px-3 text-[#4E6974] font-medium">Country</th>
                        <th className="text-left py-2 px-3 text-[#4E6974] font-medium">Sector</th>
                        <th className="text-center py-2 px-3 text-[#4E6974] font-medium">Risk</th>
                        <th className="text-center py-2 px-3 text-[#4E6974] font-medium">Sanctions</th>
                        <th className="text-right py-2 px-3 text-[#4E6974] font-medium">Spend</th>
                        <th className="text-center py-2 px-3 text-[#4E6974] font-medium">Tier</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((s) => {
                        const band = getRiskBand(s.risk_score || 0);
                        const impact = getDisruptionEstimate(s.annual_spend_aud);
                        return (
                          <tr key={s.id} className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA] cursor-pointer transition-colors" onClick={() => navigate(`/suppliers/${s.id}`)} data-testid={`supplier-row-${s.id}`}>
                            <td className="py-3 px-3 font-medium text-[#0D3B4F]">{s.supplier_name}</td>
                            <td className="py-3 px-3 text-[#4E6974]"><span className="mr-1">{COUNTRY_FLAGS[s.country_code] || ''}</span>{s.country_name}</td>
                            <td className="py-3 px-3 text-[#4E6974] text-xs">{s.primary_sector}</td>
                            <td className="py-3 px-3 text-center">
                              <Badge className={`${band.bg} ${band.text} text-xs`}>{Math.round(s.risk_score || 0)}</Badge>
                              {s.annual_spend_aud > 0 && (s.risk_score || 0) >= 50 && impact && (
                                <p className="text-[9px] text-[#FF9500] mt-0.5">~${impact.toLocaleString()} 2-wk impact</p>
                              )}
                            </td>
                            <td className="py-3 px-3 text-center" data-testid={`sanctions-status-${s.id}`}>
                              {s.sanctions_status === 'clear' && <ShieldCheck className="h-4 w-4 text-[#2E7D32] mx-auto" title="Clear - no DFAT sanctions match" />}
                              {s.sanctions_status === 'match' && <ShieldAlert className="h-4 w-4 text-[#FF3B30] mx-auto" title="Match found on DFAT sanctions list" />}
                              {s.sanctions_status === 'potential_match' && <Shield className="h-4 w-4 text-[#FF9500] mx-auto" title="Potential match - review required" />}
                              {!s.sanctions_status && <Shield className="h-4 w-4 text-[#CFD8DC] mx-auto" title="Not yet screened" />}
                            </td>
                            <td className="py-3 px-3 text-right text-[#4E6974]">{fmtSpend(s.annual_spend_aud || 0)}</td>
                            <td className="py-3 px-3 text-center text-[#4E6974] text-xs">{s.tier || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload suppliers CSV</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#4E6974]">Upload a CSV or XLSX file with columns: Supplier name (required), Country (required), City, Sector, Commodities, HS code, Annual spend (AUD), Tier.</p>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/suppliers/template`} className="text-sm text-[#0097A7] hover:underline">Download CSV template</a>
            <Input type="file" accept=".csv,.xlsx" onChange={handleUpload} disabled={uploading} data-testid="upload-file-input" />
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Supplier Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add supplier</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Supplier name *</Label><Input value={newSupplier.supplier_name} onChange={e => setNewSupplier(p => ({...p, supplier_name: e.target.value}))} data-testid="add-name-input" /></div>
            <div><Label>Country *</Label><Input value={newSupplier.country} onChange={e => setNewSupplier(p => ({...p, country: e.target.value}))} data-testid="add-country-input" /></div>
            <div><Label>City/region</Label><Input value={newSupplier.city} onChange={e => setNewSupplier(p => ({...p, city: e.target.value}))} /></div>
            <div><Label>Primary sector</Label><Input value={newSupplier.primary_sector} onChange={e => setNewSupplier(p => ({...p, primary_sector: e.target.value}))} /></div>
            <div><Label>Annual spend (AUD)</Label><Input type="number" value={newSupplier.annual_spend_aud} onChange={e => setNewSupplier(p => ({...p, annual_spend_aud: e.target.value}))} /></div>
          </div>
          <DialogFooter><Button onClick={handleAddSupplier} disabled={!newSupplier.supplier_name || !newSupplier.country} data-testid="add-supplier-submit">Add supplier</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuppliersPage;
